import { lazy, Suspense, useEffect } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, Outlet } from "react-router-dom";
import { LoadingFallback } from "@/components/LoadingFallback";
import { PWAUpdatePrompt } from '@/components/PWAUpdatePrompt';
import { checkAPIHealth, checkAuthHealth } from '@/utils/healthCheck';
import { useAppSelector } from '@/store/hooks';
import { AppLayout } from '@/components/AppLayout';
import { HeaderProvider, useHeader } from '@/contexts/HeaderContext';

// Eagerly load critical components for initial page load
import Index from "./pages/Index";
import NotFound from "./pages/NotFound";
import MainDashboard from "./pages/mainDashboard";
import { LandingHeader } from "./components/landing/landing-header";
import { LandingFooter } from "./components/landing/landing-footer";
import { ScrollToTop } from "./components/ScrollToTop";

// Lazy load authentication pages
const SignIn = lazy(() => import("./pages/SignIn"));
const SignUp = lazy(() => import("./pages/SignUp"));
const MobileLogin = lazy(() => import("./pages/MobileLogin"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const ChangePassword = lazy(() => import("./pages/ChangePassword"));

// Lazy load public pages
const DiscoverEvents = lazy(() => import("./pages/DiscoverEvents"));
const EventDetail = lazy(() => import("./pages/EventDetail"));
const Portfolio = lazy(() => import("./pages/Portfolio"));
const Pricing = lazy(() => import("./pages/Pricing"));
const Contact = lazy(() => import("./pages/Contact"));
const Terms = lazy(() => import("./pages/Terms"));
const Privacy = lazy(() => import("./pages/Privacy"));
const About = lazy(() => import("./pages/About"));

// Lazy load dashboard pages
const Dashboard = lazy(() => import("./pages/Dashboard"));
const PromoterDashboard = lazy(() => import("./pages/PromoterDashboard"));
const AttendeeDashboard = lazy(() => import("./pages/AttendeeDashboard"));
const PublishingDashboard = lazy(() => import("./pages/PublishingDashboard"));

// Lazy load event-related pages
const GenerateEvent = lazy(() => import("./pages/GenerateEvent"));
const EventsPage = lazy(() => import("./pages/EventsPage"));
const EventView = lazy(() => import("./pages/EventView"));
const EventManagement = lazy(() => import("./pages/EventManagement"));

// Lazy load booking pages
const SeatSelection = lazy(() => import("./pages/SeatSelection"));
const BookingCheckout = lazy(() => import("./pages/BookingCheckout"));
const BookingSuccess = lazy(() => import("./pages/BookingSuccess"));
const BookingFailed = lazy(() => import("./pages/BookingFailed"));
const BookingHistory = lazy(() => import("./pages/BookingHistory"));

// Lazy load analytics and management pages
const AnalyticsPage = lazy(() => import("./pages/AnalyticsPage"));
const ExportPage = lazy(() => import("./pages/ExportPage"));
const GitHubPage = lazy(() => import("./pages/GitHubPage"));
const Settings = lazy(() => import("./pages/Settings"));
const CustomerManagement = lazy(() => import("./pages/CustomerManagement"));
const UserManagement = lazy(() => import("./pages/UserManagement"));

// Lazy load development and builder pages
const Templates = lazy(() => import("./pages/Templates"));
const AdvancedTemplateStudio = lazy(() => import("./pages/AdvancedTemplateStudio"));
const CodeViewer = lazy(() => import("./pages/CodeViewer"));
const CodeEditor = lazy(() => import("./pages/CodeEditor"));
const DragDropBuilder = lazy(() => import("./pages/DragDropBuilder"));
const Deploy = lazy(() => import("./pages/Deploy"));

// Lazy load additional features
const EmailMarketing = lazy(() => import("./pages/EmailMarketing"));
const AIAssistant = lazy(() => import("./pages/AIAssistant"));
const IntegrationHub = lazy(() => import("./pages/IntegrationHub"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60 * 1000, // 1 minute
      gcTime: 5 * 60 * 1000, // 5 minutes (formerly cacheTime)
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

// Public Layout Component (with header and footer)
const PublicLayout = () => {
  return (
    <div className="min-h-screen w-full bg-background overflow-x-hidden">
      <div className="w-full relative z-50">
        <LandingHeader />
      </div>
      <Outlet />
      <footer className="w-full relative">
        <LandingFooter />
      </footer>
    </div>
  );
};

// Protected Layout Props Interface
interface ProtectedLayoutProps {
  title?: string;
  description?: string;
}

// Protected Route Component with AppLayout
const ProtectedLayout = ({
  title,
  description,
}: ProtectedLayoutProps) => {
  const { isAuthenticated, token } = useAppSelector((state) => state.auth);
  const { customHeader, useCustomHeader } = useHeader(); // Get from context

  if (!isAuthenticated || !token) {
    return <Navigate to="/signin" replace />;
  }

  return (
    <AppLayout
      title={title || "Dashboard - Swaraah"}
      description={description || "Manage your AI-generated event websites"}
      useCustomHeader={useCustomHeader}
      customHeader={customHeader}
    >
      <Outlet />
    </AppLayout>
  );
};

// Auth Route Component (redirects to dashboard if already logged in)
const AuthLayout = () => {
  const { isAuthenticated } = useAppSelector((state) => state.auth);

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return <Outlet />;
};

const App = () => {
  useEffect(() => {
    // Run health checks on app load
    const runHealthChecks = async () => {
      const apiUrl = import.meta.env.VITE_API_URL || '/api';

      console.log('🚀 Swaraah AI - Starting Application');
      console.log('=================================');
      console.log('Environment:', import.meta.env.MODE);
      console.log('API URL:', apiUrl);
      console.log('=================================');

      try {
        const apiHealthy = await checkAPIHealth(apiUrl);
        const authHealthy = await checkAuthHealth(apiUrl);

        if (!apiHealthy || !authHealthy) {
          console.warn('⚠️ Some services are not responding');
        } else {
          console.log('✅ All services are healthy');
        }
      } catch (error) {
        console.error('❌ Health check failed:', error);
      }
    };

    runHealthChecks();
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <HeaderProvider>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <PWAUpdatePrompt />
          <BrowserRouter>
            <ScrollToTop /> {/* Add this line */}
            <Suspense fallback={<LoadingFallback />}>
              <Routes>
                {/* Public routes with header and footer */}
                <Route element={<PublicLayout />}>
                  <Route path="/" element={<Index />} />
                  <Route path="/discover" element={<DiscoverEvents />} />
                  <Route path="/events/:id" element={<EventDetail />} />
                  <Route path="/event/:id" element={<EventView />} />
                  <Route path="/portfolio" element={<Portfolio />} />
                  <Route path="/pricing" element={<Pricing />} />
                  <Route path="/contact" element={<Contact />} />
                  <Route path="/terms" element={<Terms />} />
                  <Route path="/privacy" element={<Privacy />} />
                  <Route path="/about" element={<About />} />
                </Route>

                {/* Auth routes - Redirect to dashboard if already logged in */}
                <Route element={<AuthLayout />}>
                  <Route path="/signin" element={<SignIn />} />
                  <Route path="/signup" element={<SignUp />} />
                  <Route path="/mobile-login" element={<MobileLogin />} />
                  <Route path="/forgot-password" element={<ForgotPassword />} />
                </Route>

                {/* Reset password - standalone (no layout) */}
                <Route path="/reset-password" element={<ResetPassword />} />

                {/* Booking flow - Partial authentication */}
                <Route path="/booking/:eventId/seats" element={<SeatSelection />} />
                <Route path="/booking/failed" element={<BookingFailed />} />

                {/* Protected booking routes with AppLayout */}
                <Route element={<ProtectedLayout title="Booking - Swaraah" description="Complete your booking" />}>
                  <Route path="/booking/:eventId/checkout" element={<BookingCheckout />} />
                  <Route path="/booking/success" element={<BookingSuccess />} />
                  <Route path="/bookings" element={<BookingHistory />} />
                </Route>

                {/* Protected Dashboard routes with AppLayout */}
                <Route element={<ProtectedLayout title="Dashboard - Swaraah" description="Manage your AI-generated event websites" />}>
                  <Route path="/dashboard" element={<Dashboard />} />
                  <Route path="/promoter-dashboard" element={<PromoterDashboard />} />
                  <Route path="/main-dashboard" element={<MainDashboard />} />
                  <Route path="/attendee-dashboard" element={<AttendeeDashboard />} />
                  <Route path="/publishing" element={<PublishingDashboard />} />
                </Route>

                {/* Protected Event management with AppLayout */}
                <Route element={<ProtectedLayout title="Event Management - Swaraah" description="Create and manage your events" />}>
                  <Route path="/generate" element={<GenerateEvent />} />
                  <Route path="/events" element={<EventsPage />} />
                  <Route path="/event-management" element={<EventManagement />} />
                </Route>

                {/* Development tools - Standard Header */}
                <Route element={<ProtectedLayout title="Development Tools - Swaraah" description="Build and customize your event websites" />}>
                  <Route path="/templates" element={<Templates />} />
                  <Route path="/template-studio" element={<AdvancedTemplateStudio />} />
                  <Route path="/code-viewer" element={<CodeViewer />} />
                  <Route path="/builder" element={<DragDropBuilder />} />
                  <Route path="/deploy" element={<Deploy />} />
                </Route>

                {/* Code Editor - Custom Header (context handles it) */}
                <Route element={<ProtectedLayout title="Code Editor - Swaraah" description="Edit and customize code" />}>
                  <Route path="/code-editor" element={<CodeEditor />} />
                </Route>

                {/* Protected Analytics and management with AppLayout */}
                <Route element={<ProtectedLayout title="Analytics & Management - Swaraah" description="View insights and manage your platform" />}>
                  <Route path="/analytics" element={<AnalyticsPage />} />
                  <Route path="/export" element={<ExportPage />} />
                  <Route path="/github" element={<GitHubPage />} />
                  <Route path="/settings" element={<Settings />} />
                  <Route path="/change-password" element={<ChangePassword />} />
                  <Route path="/customers" element={<CustomerManagement />} />
                  <Route path="/users" element={<UserManagement />} />
                </Route>

                {/* Protected Additional features with AppLayout */}
                <Route element={<ProtectedLayout title="Features - Swaraah" description="Advanced features for your events" />}>
                  <Route path="/email-marketing" element={<EmailMarketing />} />
                  <Route path="/ai-assistant" element={<AIAssistant />} />
                  <Route path="/integrations" element={<IntegrationHub />} />
                </Route>

                {/* 404 - Catch all */}
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
          </BrowserRouter>
        </TooltipProvider>
      </HeaderProvider>
    </QueryClientProvider>
  );
};

export default App;
